import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ComponentType } from 'react';
import { creerClient, type Client } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { COULEURS, styleBouton } from '../../app/styles.js';
import { PAGES_ARRIVEE, libelleArrivee } from './apercu-lien.js';
import type { PageArrivee } from './contrat.js';
import { creerControleurBoite, creerControleurRelances } from './controleurs.js';
import { BoutonEnvoyer, VueApercuLien, VueEtatListe, VueFil, VueModele, VueRattachement, VueRelances } from './vues.js';

/** Le client de la coquille peut être fourni ici ; à défaut, les écrans appellent l'API du même site. */
export const ContexteClientMessages = createContext<Client | null>(null);

function useClient(): Client {
  const fourni = useContext(ContexteClientMessages);
  return useMemo(() => fourni ?? creerClient({ fetch: (...args) => globalThis.fetch(...args) }), [fourni]);
}

const Retour = ({ message }: { message: string | null }) =>
  message === null ? null : (
    <p role="status" style={{ color: COULEURS.marine }}>
      {message}
    </p>
  );

function EcranBoite({ ecran }: { ecran: Ecran }) {
  const client = useClient();
  const c = useMemo(() => creerControleurBoite(client), [client]);
  const etat = useSyncExternalStore(c.subscribe, c.getEtat, c.getEtat);
  useEffect(() => void c.charger(), [c]);

  return (
    <section data-ecran={ecran.id} aria-label={ecran.titre}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      <VueEtatListe
        etat={etat.liste}
        nombre={etat.fils?.length ?? 0}
        chargement="Chargement des conversations…"
        vide="Aucune conversation pour le moment."
        onReessayer={c.reessayer}
      >
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(etat.fils ?? []).map((f) => (
            <li key={f.id}>
              <button type="button" onClick={() => void c.ouvrir(f)} style={{ ...styleBouton, width: '100%', textAlign: 'left' }}>
                {`${f.channelAccount.label} · ${f.externalContact} · ${f.lastMessagePreview}`}
                {f.unreadCount > 0 ? ` (${f.unreadCount} non lu)` : ''}
                {f.status === 'non_rattache' ? ' · à rattacher' : ''}
              </button>
            </li>
          ))}
        </ul>
      </VueEtatListe>
      {etat.ouvert ? (
        <>
          <VueFil fil={etat.ouvert.detail} />
          <VueRattachement
            fil={etat.ouvert.fil}
            rattachement={etat.rattachement}
            onDemarrer={c.demarrerRattachement}
            onChoisirDossier={c.choisirDossier}
            onChercher={c.chercherPersonnes}
            onRattacher={c.rattacher}
            onAnnuler={c.annulerRattachement}
          />
          <label>
            <span style={{ display: 'block', fontWeight: 600 }}>Votre réponse</span>
            <textarea
              value={etat.texte}
              rows={4}
              onChange={(e) => c.saisir(e.target.value)}
              style={{ width: '100%', font: 'inherit', boxSizing: 'border-box' }}
            />
          </label>
          <p style={{ color: COULEURS.ardoise }}>Le message est signé « Cabinet MAAT ».</p>
          <label>
            <span style={{ fontWeight: 600 }}>Lien de l’application, page d’arrivée </span>
            <select value={etat.page} onChange={(e) => c.choisirPage(e.target.value as PageArrivee)} style={{ minHeight: 44 }}>
              {PAGES_ARRIVEE.map((p) => (
                <option key={p} value={p}>
                  {libelleArrivee(p)}
                </option>
              ))}
            </select>
          </label>{' '}
          <button type="button" onClick={() => void c.voirLien()} style={styleBouton}>
            Voir l’aperçu du lien
          </button>
          {etat.apercu ? <VueApercuLien apercu={etat.apercu} /> : null}
          <p>
            <BoutonEnvoyer enCours={etat.envoiEnCours} onEnvoyer={() => void c.envoyer()} />
          </p>
        </>
      ) : null}
      <Retour message={etat.retour} />
    </section>
  );
}

function EcranRelances({ ecran }: { ecran: Ecran }) {
  const client = useClient();
  const c = useMemo(() => creerControleurRelances(client), [client]);
  const etat = useSyncExternalStore(c.subscribe, c.getEtat, c.getEtat);
  useEffect(() => void c.charger(), [c]);

  return (
    <section data-ecran={ecran.id} aria-label={ecran.titre}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      <label>
        <span style={{ fontWeight: 600 }}>Motif de la pause </span>
        <input value={etat.motif} onChange={(e) => c.saisirMotif(e.target.value)} style={{ minHeight: 44 }} />
      </label>
      <VueEtatListe
        etat={etat.listePlans}
        nombre={etat.plans?.length ?? 0}
        chargement="Chargement des relances…"
        vide="Aucune relance en cours."
        onReessayer={c.reessayer}
      >
        <VueRelances
          plans={etat.plans ?? []}
          simulation={etat.simulation}
          onPause={(p) => void c.pause(p)}
          onReprise={(p) => void c.reprise(p)}
          onSimuler={(p) => void c.simuler(p)}
        />
      </VueEtatListe>
      <h2 style={{ color: COULEURS.marine }}>Modèles de messages</h2>
      <VueEtatListe
        etat={etat.listeModeles}
        nombre={etat.modeles.length}
        chargement="Chargement des modèles…"
        vide="Aucun modèle."
        onReessayer={c.reessayer}
      >
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {etat.modeles.map((m) => (
            <li key={m.code}>
              <button type="button" onClick={() => c.choisirModele(m)} style={styleBouton}>
                {`${m.code} · ${m.channel} · version ${m.versionNo}`}
              </button>
            </li>
          ))}
        </ul>
      </VueEtatListe>
      {etat.choisi ? (
        <VueModele
          modele={etat.choisi}
          corps={etat.corps}
          controles={c.controles()}
          onChanger={c.modifierCorps}
          onEnregistrer={() => void c.enregistrer()}
        />
      ) : null}
      <Retour message={etat.retour} />
    </section>
  );
}

export const ecrans: Readonly<Record<string, ComponentType<{ ecran: Ecran }>>> = { D06: EcranBoite, D07: EcranRelances };
